// Table of totalsByModel, expandable per row to reveal that model's own
// day-by-day series for the selected period.
import { Text, Caption1, makeStyles, tokens } from '@fluentui/react-components';
import { ExpandableTable } from '../ExpandableTable';
import type { Column } from '../ExpandableTable';
import type { DailyUsageMap, ModelUsageMap } from '../../types/tokenusage';
import { formatCount } from './formatUsage';

const useStyles = makeStyles({
  dailyList: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
    gap: `2px ${tokens.spacingHorizontalM}`,
  },
  dailyRow: { display: 'flex', justifyContent: 'space-between', gap: tokens.spacingHorizontalS },
  dayLabel: { color: tokens.colorNeutralForeground3 },
});

interface ModelRow {
  model: string;
  totalTokens: number;
  requests: number;
}

export interface ModelBreakdownTableProps {
  totalsByModel: ModelUsageMap;
  dailyByModel: DailyUsageMap;
  loading?: boolean;
}

function rowsFromTotals(totalsByModel: ModelUsageMap): ModelRow[] {
  return Object.entries(totalsByModel)
    .map(([model, stat]) => ({ model, totalTokens: stat.totalTokens, requests: stat.requests }))
    .sort((a, b) => b.totalTokens - a.totalTokens);
}

/** Extract a model's own day-by-day totals from the daily-by-model map, sorted by date ascending. */
function dailySeriesForModel(dailyByModel: DailyUsageMap, model: string): Array<{ day: string; totalTokens: number; requests: number }> {
  return Object.entries(dailyByModel)
    .filter(([, models]) => models[model] != null)
    .map(([day, models]) => ({ day, totalTokens: models[model].totalTokens, requests: models[model].requests }))
    .sort((a, b) => a.day.localeCompare(b.day));
}

export function ModelBreakdownTable(props: ModelBreakdownTableProps) {
  const { totalsByModel, dailyByModel, loading } = props;
  const styles = useStyles();
  const rows = rowsFromTotals(totalsByModel);

  const columns: Column<ModelRow>[] = [
    { key: 'model', label: 'Model', render: (r) => r.model, sortable: true, filterable: true, getValue: (r) => r.model },
    {
      key: 'totalTokens',
      label: 'Tokens',
      render: (r) => formatCount(r.totalTokens),
      sortable: true,
      getValue: (r) => String(r.totalTokens).padStart(20, '0'),
    },
    {
      key: 'requests',
      label: 'Requests',
      render: (r) => formatCount(r.requests),
      sortable: true,
      getValue: (r) => String(r.requests).padStart(20, '0'),
    },
  ];

  return (
    <ExpandableTable<ModelRow>
      columns={columns}
      items={rows}
      getRowKey={(r) => r.model}
      loading={loading}
      emptyMessage="No model usage recorded for this period."
      searchPlaceholder="Search models…"
      defaultSortKey="totalTokens"
      defaultSortDir="desc"
      renderExpanded={(row) => {
        const series = dailySeriesForModel(dailyByModel, row.model);
        if (series.length === 0) {
          return <Caption1>No daily detail available for {row.model}.</Caption1>;
        }
        return (
          <div className={styles.dailyList}>
            {series.map((d) => (
              <div key={d.day} className={styles.dailyRow}>
                <Caption1 className={styles.dayLabel}>{d.day}</Caption1>
                <Text size={200}>{formatCount(d.totalTokens)}</Text>
              </div>
            ))}
          </div>
        );
      }}
    />
  );
}

export default ModelBreakdownTable;
